import { Grid, Stack, Typography } from "@mui/material";
import { FC, memo } from "react";
import { useFormContext, useWatch } from "react-hook-form";

import { CollapsibleCard } from "@/components/elements/CollapsibleCard";
import { FormField } from "@/modules/irohana-components/elements/FormField";
import { Input } from "@/modules/irohana-components/elements/Input";
import { RadioGroup } from "@/modules/irohana-components/elements/RadioGroup";
import { Select } from "@/modules/irohana-components/elements/Select";
import { Switch } from "@/modules/irohana-components/elements/Switch";
import { Textarea } from "@/modules/irohana-components/elements/Textarea";
import { text } from "@/theme/colors";
import {
  BILLING_CYCLE_RADIO_OPTIONS,
  INVOICE_DISPLAY_FORMAT_RADIO_OPTIONS,
  PRODUCT_UNIT_OPTIONS,
} from "@/features/organization/product/constants/productEnums";
import { TProductForm } from "@/api/services/main/organization/product/dtos/product.dto";
import { showsFirstDiscount } from "@/features/organization/product/utils/productUtils";
import { useProductMasters } from "@/features/organization/product/hooks/useProductMasters";

type TProductInformationSectionProps = {
  /** 固定商品: 商品名 & 請求サイクル are locked; 税区分 stays editable (C1). */
  isDefault?: boolean;
};

const BILLING_TIP = "スポットは都度請求、継続は毎月自動で請求されます。";
const INVOICE_TIP = "明細は受注別に項目化、集約は1項目にまとめて請求書へ出力します。";

/** 商品基本情報 — editable card (登録・編集). */
export const ProductInformationSection: FC<TProductInformationSectionProps> = memo(({ isDefault = false }) => {
  const { control } = useFormContext<TProductForm>();
  const { taxRateOptions } = useProductMasters();
  const name = useWatch({ control, name: "name" });
  const showFirstDiscount = showsFirstDiscount(name ?? "");

  const title = (
    <Typography variant="body14Bold" color={text.primary}>
      商品基本情報
    </Typography>
  );

  return (
    <CollapsibleCard title={title}>
      <Grid container columnSpacing={3} rowSpacing={2}>
        <Grid item xs={12} md={6}>
          <FormField control={control} name="name" label="商品名" required>
            <Input readonly={isDefault} placeholder="入力してください" />
          </FormField>
        </Grid>
        <Grid item xs={12} md={6}>
          <FormField control={control} name="accountCategory" label="科目区分">
            <Input placeholder="入力してください" />
          </FormField>
        </Grid>

        <Grid item xs={12} md={6}>
          <FormField control={control} name="unit" label="単位">
            <Select options={PRODUCT_UNIT_OPTIONS} clearable placeholder="選択してください" />
          </FormField>
        </Grid>
        <Grid item xs={12} md={6}>
          <FormField control={control} name="unitPrice" label="単価（税抜）" required>
            <Input placeholder="入力してください" endAdornment="円" clearable={false} />
          </FormField>
        </Grid>

        <Grid item xs={12} md={6}>
          <FormField control={control} name="discountTokutei2" label="値引き（特定2号）">
            <Input placeholder="入力してください" endAdornment="円" clearable={false} />
          </FormField>
        </Grid>
        <Grid item xs={12} md={6}>
          <FormField control={control} name="taxRateId" label="税区分" required>
            <Select options={taxRateOptions} placeholder="選択してください" />
          </FormField>
        </Grid>

        {showFirstDiscount && (
          <>
            <Grid item xs={12} md={6}>
              <FormField control={control} name="firstDiscountPeriod" label="初回割引期間">
                <Input placeholder="入力してください" endAdornment="ヶ月" clearable={false} />
              </FormField>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormField control={control} name="firstDiscountPrice" label="初回割引単価">
                <Input placeholder="入力してください" endAdornment="円" clearable={false} />
              </FormField>
            </Grid>
          </>
        )}

        <Grid item xs={12} md={6}>
          <FormField
            control={control}
            name="billingCycle"
            label="請求サイクル"
            required
            helper={BILLING_TIP}
            helperContent={<Typography variant="body14">{BILLING_TIP}</Typography>}
          >
            <Stack gap={0.5}>
              <RadioGroup options={BILLING_CYCLE_RADIO_OPTIONS} disabled={isDefault} />
              {isDefault && (
                <Typography variant="cap12" sx={{ color: text.accent_orange }}>
                  固定商品のため請求サイクルは変更できません。
                </Typography>
              )}
            </Stack>
          </FormField>
        </Grid>
        <Grid item xs={12} md={6}>
          <FormField
            control={control}
            name="invoiceDisplayFormat"
            label="請求書上の表示形式"
            helper={INVOICE_TIP}
            helperContent={<Typography variant="body14">{INVOICE_TIP}</Typography>}
          >
            <RadioGroup options={INVOICE_DISPLAY_FORMAT_RADIO_OPTIONS} />
          </FormField>
        </Grid>

        <Grid item xs={12} md={6}>
          <FormField control={control} name="isActive" label="商品設定" sx={{ "& .MuiFormControlLabel-root": { ml: 0 } }}>
            <Switch />
          </FormField>
        </Grid>

        <Grid item xs={12}>
          <FormField control={control} name="description" label="メモ">
            <Textarea minRows={3} maxRows={6} placeholder="入力してください" />
          </FormField>
        </Grid>
      </Grid>
    </CollapsibleCard>
  );
});

ProductInformationSection.displayName = "ProductInformationSection";
